import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  LogOut,
  Loader2,
  Phone,
  Mail,
  MapPin,
  MessageSquare,
  Clock,
  ArrowRight,
  CheckCircle2,
  XCircle,
  ChevronRight,
  ChevronDown,
  ShieldCheck,
  ShieldAlert,
  ShieldX,
  Lock,
  Upload,
  FileText,
  ImageIcon,
  Trash2,
  DollarSign,
  Briefcase,
  ExternalLink,
  Star,
  Calendar as CalendarIcon,
  Search,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { useAuth } from "@/context/AuthContext";
import MessageBoard from "@/components/MessageBoard";
import UnreadBadge from "@/components/UnreadBadge";
import { LineItemsEditor } from "@/pages/ProCRM";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COLUMNS = [
  { id: "new", label: "New", accent: "#0E93EC" },
  { id: "contacted", label: "Contacted", accent: "#C9C6C6" },
  { id: "quoted", label: "Quoted", accent: "#EEEEEF" },
  { id: "won", label: "Won", accent: "#0E2F5F" },
  { id: "lost", label: "Lost", accent: "#8B7A6E" },
];

// Canonical trade list (matches homeowner request form options)
const TRADE_OPTIONS = [
  "Plumbing",
  "Electrical",
  "HVAC",
  "Roofing",
  "Painting",
  "Landscaping",
  "Handyman",
  "Carpentry",
  "Flooring",
  "Cleaning",
  "Remodeling",
  "General Contractor",
  "Other",
];

function timeAgo(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const s = Math.floor((Date.now() - t) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

/* ============================================================
 *  Verification Panel — upload docs, see verification status
 * ============================================================ */
const DOC_TYPES = [
  { id: "license", label: "License" },
  { id: "insurance", label: "Insurance" },
  { id: "id", label: "Government ID" },
  { id: "certification", label: "Certification" },
  { id: "photo", label: "Workplace photo" },
  { id: "other", label: "Other" },
];

function VerificationPanel({ onStatusChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState("license");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/workspace/verifications`);
      setData(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File too large (max 5 MB)");
      return;
    }
    setUploading(true);
    try {
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await axios.post(`${API}/workspace/verifications/upload`, {
        doc_type: docType,
        filename: file.name,
        mime_type: file.type || "application/octet-stream",
        content_base64: b64,
      });
      toast.success("Document uploaded");
      await load();
      onStatusChange?.();
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Could not upload document"
      );
    } finally {
      setUploading(false);
    }
  };

  const removeDoc = async (docId) => {
    try {
      await axios.delete(`${API}/workspace/verifications/${docId}`);
      toast.success("Document removed");
      load();
    } catch {
      toast.error("Could not remove");
    }
  };

  if (loading) return null;

  const status = data?.verification_status || "unverified";
  const docs = data?.docs || [];

  const badge = {
    unverified: {
      icon: Lock,
      color: "bg-[#EEEEEF]/15 text-white",
      title: "Not verified yet",
      copy: "Upload your license, insurance, and ID so homeowners know you're the real deal. Verified pros get up to 3× more calls.",
    },
    pending: {
      icon: ShieldAlert,
      color: "bg-[#0E93EC] text-white",
      title: "Awaiting admin review",
      copy: "Thanks — we've received your documents. An admin will review them shortly.",
    },
    verified: {
      icon: ShieldCheck,
      color: "bg-[#0E93EC] text-[#EEEEEF]",
      title: "Verified pro",
      copy: "Your profile shows a Verified badge to every homeowner. Congrats!",
    },
    rejected: {
      icon: ShieldX,
      color: "bg-[#010204] text-white",
      title: "Verification rejected",
      copy: data?.rejection_reason || "Please re-upload updated documents.",
    },
  }[status];
  const Icon = badge.icon;

  return (
    <section
      className="max-w-[1600px] mx-auto px-6 lg:px-10 pt-8"
      data-testid="verification-panel"
    >
      <div className="bg-[#0D1421] border border-[#C9C6C6]/10">
        <div className="p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-[#C9C6C6]/10">
          <div className="flex items-start gap-4 min-w-0">
            <div className={`w-11 h-11 flex items-center justify-center shrink-0 ${badge.color}`}>
              <Icon className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-1">
                Verification
              </div>
              <div className="font-editorial text-2xl font-normal text-[#EEEEEF] leading-tight">
                {badge.title}
              </div>
              <p className="mt-1 text-sm text-[#C9C6C6] leading-relaxed max-w-lg">
                {badge.copy}
              </p>
            </div>
          </div>
          <div
            className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-semibold ${badge.color}`}
            data-testid={`verification-status-${status}`}
          >
            {status}
          </div>
        </div>

        {status !== "verified" && (
          <div className="p-6 flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1">
              <Label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
                Document type
              </Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger
                  className="mt-2 bg-[#010204] border-0 rounded-none h-12 focus:ring-2 focus:ring-[#0E93EC]"
                  data-testid="verification-doc-type"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DOC_TYPES.map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label
              htmlFor="verification-file"
              className={`inline-flex items-center gap-3 bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-12 px-6 cursor-pointer font-medium text-sm ${
                uploading ? "opacity-70 pointer-events-none" : ""
              }`}
              data-testid="verification-upload-btn"
            >
              {uploading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Upload className="w-4 h-4" />
              )}
              {uploading ? "Uploading…" : "Upload document"}
            </label>
            <input
              id="verification-file"
              type="file"
              accept="image/*,application/pdf"
              onChange={handleFile}
              className="hidden"
              data-testid="verification-file-input"
            />
          </div>
        )}

        {docs.length > 0 && (
          <div className="border-t border-[#C9C6C6]/10 p-6" data-testid="verification-docs-list">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-4">
              Uploaded documents ({docs.length})
            </div>
            <div className="space-y-2">
              {docs.map((d) => (
                <div
                  key={d.id}
                  className="flex items-center justify-between gap-4 border border-[#C9C6C6]/10 px-4 py-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {d.mime_type?.startsWith("image/") ? (
                      <ImageIcon className="w-4 h-4 text-[#0E93EC] shrink-0" />
                    ) : (
                      <FileText className="w-4 h-4 text-[#0E93EC] shrink-0" />
                    )}
                    <div className="min-w-0">
                      <div className="text-sm text-[#EEEEEF] truncate font-medium">
                        {d.filename}
                      </div>
                      <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9C6C6]">
                        {d.doc_type} · {Math.round(d.size_bytes / 1024)} KB
                      </div>
                    </div>
                  </div>
                  {status !== "verified" && (
                    <button
                      onClick={() => removeDoc(d.id)}
                      className="p-2 text-[#C9C6C6] hover:text-[#EEEEEF]"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

/* ============================================================
 *  Top Bar
 * ============================================================ */
function TopBar({ user, onLogout }) {
  return (
    <header className="border-b border-[#C9C6C6]/8 bg-[#0D1421] sticky top-0 z-30">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0">
          <img src="/brand/TPWH_Logo_Transparent.png" alt="The Pros Work Hub" className="h-9 w-auto shrink-0" data-testid="brand-logo-img" />
          <span className="hidden md:inline-block ml-3 text-[11px] uppercase tracking-[0.22em] text-[#C9C6C6] border-l border-[#C9C6C6]/25 pl-3">
            Workspace
          </span>
        </Link>
        <div className="flex items-center gap-2 sm:gap-4 shrink-0">
          <Link
            to="/workspace/calendar"
            className="inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C9C6C6] hover:text-[#0E93EC] border border-[#C9C6C6]/10 hover:border-[#0E93EC] px-2.5 py-1.5 transition-colors"
            data-testid="workspace-calendar-link"
            title="Open your job calendar"
          >
            <CalendarIcon className="w-3.5 h-3.5" />
            <span className="hidden md:inline">Calendar</span>
          </Link>
          {user?.picture && (
            <img
              src={user.picture}
              alt={user.name || user.email}
              className="w-8 h-8 rounded-full ring-1 ring-[#C9C6C6]/10"
            />
          )}
          <div className="hidden md:block text-right">
            <div className="text-sm text-[#EEEEEF] font-medium leading-tight flex items-center gap-1.5">
              {user?.name || "Pro"}
              {user?.verification_status === "verified" && (
                <span
                  className="inline-flex items-center gap-0.5 bg-[#0E93EC] text-[#EEEEEF] text-[9px] uppercase tracking-[0.18em] font-semibold px-1.5 py-0.5"
                  data-testid="verified-badge"
                  title="Verified pro"
                >
                  <ShieldCheck className="w-2.5 h-2.5" />
                  Verified
                </span>
              )}
            </div>
            <div className="text-[11px] text-[#C9C6C6]">{user?.email}</div>
          </div>
          {user?.id && (
            <Link
              to={`/pro/${user.id}`}
              target="_blank"
              rel="noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-[10px] sm:text-[11px] uppercase tracking-[0.2em] font-semibold text-[#C9C6C6] hover:text-[#EEEEEF] border border-[#C9C6C6]/10 px-2.5 py-1.5"
              data-testid="workspace-public-profile-link"
              title="View & share your public pro profile"
            >
              <ExternalLink className="w-3 h-3" />
              <span className="hidden md:inline">Public profile</span>
            </Link>
          )}
          <Button
            onClick={onLogout}
            variant="ghost"
            className="rounded-none text-[#C9C6C6] hover:text-[#EEEEEF] gap-2 px-2 sm:px-4"
            data-testid="workspace-logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="hidden md:inline">Log out</span>
          </Button>
        </div>
      </div>
    </header>
  );
}

/* ============================================================
 *  Header Banner — subscription state, free leads left, upgrade CTA
 * ============================================================ */
function SubscriptionBanner({ user, summary }) {
  void summary;
  const isActive = user?.subscription_status === "active";

  if (isActive) {
    return (
      <div
        className="bg-[#0E93EC] text-[#EEEEEF] px-4 md:px-6 py-3 flex items-center justify-between"
        data-testid="subscription-banner-active"
      >
        <div className="flex items-center gap-3 text-sm">
          <CheckCircle2 className="w-4 h-4 text-[#0E93EC]" />
          <span>
            <b className="uppercase tracking-wide">{user?.plan}</b> plan · Active
          </span>
        </div>
        <Link
          to="/#pricing"
          className="text-[11px] uppercase tracking-[0.2em] font-medium text-[#EEEEEF]/80 hover:text-white"
        >
          Manage
        </Link>
      </div>
    );
  }

  // No active subscription — full paywall banner
  return (
    <div
      className="bg-[#010204] text-[#EEEEEF] px-4 md:px-6 py-3 flex items-center justify-between"
      data-testid="subscription-banner-paywall"
    >
      <div className="text-sm">
        <XCircle className="inline w-4 h-4 text-[#0E93EC] mr-2 mb-0.5" />
        <b>Pick a plan to start claiming leads.</b>{" "}
        <span className="text-[#EEEEEF]/70">
          Starter $40/mo · Pro $60/mo · cancel any time.
        </span>
      </div>
      <Link
        to="/#pricing"
        className="group text-[11px] uppercase tracking-[0.2em] font-medium bg-[#0E93EC] text-white px-3 py-1.5 flex items-center gap-1 hover:bg-[#0E2F5F]"
      >
        See plans
        <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

/* ============================================================
 *  Summary strip — pipeline counts
 * ============================================================ */
function SummaryStrip({ summary }) {
  const counts = summary?.counts || { new: 0, contacted: 0, quoted: 0, won: 0, lost: 0 };
  const items = [
    { label: "Available", value: summary?.total_leads_available ?? 0 },
    { label: "Claimed", value: summary?.claimed ?? 0 },
    { label: "Contacted", value: counts.contacted },
    { label: "Quoted", value: counts.quoted },
    { label: "Won", value: counts.won },
  ];
  return (
    <div className="border-b border-[#C9C6C6]/8 bg-[#0D1421]">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-6 grid grid-cols-3 sm:grid-cols-5 gap-3 sm:gap-6">
        {items.map((it) => (
          <div key={it.label} data-testid={`stat-${it.label.toLowerCase()}`}>
            <div className="font-editorial text-3xl sm:text-4xl font-light text-[#EEEEEF] tracking-tighter leading-none">
              {it.value}
            </div>
            <div className="mt-1.5 sm:mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-semibold text-[#C9C6C6]">
              {it.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


/* ============================================================
 *  FoundingUpgradeStrip — gold strip shown only to Founding Starter pros
 *  who can lock in Founding Pro at $40/mo forever.
 * ============================================================ */
function FoundingUpgradeStrip({ user }) {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workspace/profile`);
        if (alive) setProfile(data);
      } catch {
        /* silent */
      }
    })();
    return () => { alive = false; };
  }, [user?.id]);

  if (!profile) return null;
  const isFounder = !!profile.is_founding_member;
  const plan = profile.founding_plan || profile.plan;
  const canUpgrade = isFounder && plan === "founding_starter" && !profile.founding_lost;

  const upgrade = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/workspace/subscription/upgrade-to-founding-pro`,
        { origin_url: window.location.origin }
      );
      if (data?.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start upgrade");
    } finally {
      setBusy(false);
    }
  };

  // Show a "you're a founder" chip when they're already on Founding Pro
  if (isFounder && !canUpgrade) {
    return (
      <div className="border-b border-[#C9C6C6]/8 bg-gradient-to-r from-[#C9C6C6]/12 via-[#C9C6C6]/6 to-transparent" data-testid="founding-badge-strip">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-3 flex items-center gap-3">
          <div className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#C9C6C6] to-[#C9C6C6] text-[#EEEEEF] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold ring-1 ring-[#C9C6C6]/50">
            <Star className="w-3.5 h-3.5 fill-[#EEEEEF]" /> Founding Member
          </div>
          <div className="text-sm text-[#EEEEEF]">
            You&apos;re locked in on {plan === "founding_pro" ? "Founding Pro · $40/mo" : "Founding Starter · $25/mo"} for life.
          </div>
        </div>
      </div>
    );
  }

  if (!canUpgrade) return null;

  return (
    <div className="border-b border-[#C9C6C6]/8 bg-[#010204] text-[#EEEEEF]" data-testid="founding-upgrade-strip">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-4 sm:py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-start sm:items-center gap-3">
          <div className="inline-flex items-center gap-1.5 bg-gradient-to-br from-[#C9C6C6] to-[#C9C6C6] text-[#EEEEEF] px-3 py-1.5 text-[10px] uppercase tracking-[0.22em] font-bold shrink-0">
            <Star className="w-3.5 h-3.5 fill-[#EEEEEF]" /> Founder
          </div>
          <div>
            <div className="font-editorial text-lg sm:text-xl leading-tight">
              Upgrade to <span className="text-[#C9C6C6] italic">Founding Pro</span> — locked at $40/mo forever
            </div>
            <div className="text-xs text-[#C9C6C6] mt-0.5">
              Regular Pro is $60/mo. You&apos;ll save $180/year. Rate never increases.
            </div>
          </div>
        </div>
        <Button
          onClick={upgrade}
          disabled={busy}
          className="bg-[#C9C6C6] hover:bg-[#C9C6C6] text-[#EEEEEF] rounded-none h-11 px-5 font-semibold shrink-0"
          data-testid="founding-upgrade-cta"
        >
          {busy ? "Starting…" : "Upgrade for $40/mo"}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}

/* ============================================================
 *  ReferralsStrip — private profile analytics for the pro
 * ============================================================ */
function ReferralsStrip({ user }) {
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/workspace/referrals`);
        if (alive) setStats(data);
      } catch {
        /* silent — pro not authed or empty */
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (!user?.id) return null;
  const profileUrl = user.id
    ? `${window.location.origin}/pro/${user.id}?utm_source=share&utm_medium=direct`
    : "";
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(profileUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success("Profile link copied");
    } catch {
      toast.error("Could not copy");
    }
  };

  const items = [
    { label: "Profile views · 7d", value: stats?.views?.["7d"] ?? "—" },
    { label: "Profile views · 30d", value: stats?.views?.["30d"] ?? "—" },
    { label: "Referred leads · 7d", value: stats?.referred_leads?.["7d"] ?? "—" },
    { label: "Referred leads · 30d", value: stats?.referred_leads?.["30d"] ?? "—" },
  ];

  const topSources = stats?.top_sources || [];

  return (
    <div className="border-b border-[#C9C6C6]/8 bg-[#010204]" data-testid="referrals-strip">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-5 sm:py-6">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC]">
              — Your public profile
            </div>
            <div className="mt-1 text-sm text-[#EEEEEF]">
              Share your link. Every view + attributed lead lands here (private to you).
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/pro/${user.id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#010204] border border-[#C9C6C6]/10 text-xs sm:text-sm font-medium hover:border-[#0E93EC]"
              data-testid="referrals-open-profile"
            >
              <ExternalLink className="w-3.5 h-3.5" /> View
            </Link>
            <button
              onClick={copyLink}
              className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#0E93EC] hover:bg-[#0E2F5F] text-white text-xs sm:text-sm font-medium"
              data-testid="referrals-copy-link"
            >
              {copied ? "Copied ✓" : "Copy share link"}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
          {items.map((it, i) => (
            <div key={it.label} data-testid={`referral-stat-${i}`}>
              <div className="font-editorial text-3xl sm:text-4xl font-light text-[#EEEEEF] tracking-tighter leading-none">
                {it.value}
              </div>
              <div className="mt-1.5 sm:mt-2 text-[9px] sm:text-[10px] uppercase tracking-[0.18em] sm:tracking-[0.22em] font-semibold text-[#C9C6C6]">
                {it.label}
              </div>
            </div>
          ))}
        </div>

        {topSources.length > 0 && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
              Top channels
            </span>
            {topSources.map((s) => (
              <span
                key={s.source}
                className="text-[11px] px-2 py-1 bg-[#010204] border border-[#C9C6C6]/10 tracking-wide"
                data-testid={`referral-source-${s.source}`}
              >
                {s.source}
                <span className="text-[#C9C6C6] ml-1.5">· {s.count}</span>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



/* ============================================================
 *  Lead Card
 * ============================================================ */
function LeadCard({ lead, onOpen }) {
  const state = lead.state || {};
  const claimed = state.claimed;
  return (
    <motion.button
      layout
      onClick={() => onOpen(lead)}
      className="w-full text-left bg-[#0D1421] border border-[#C9C6C6]/10 p-4 hover:border-[#0E93EC] transition-colors group"
      data-testid={`lead-card-${lead.id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="min-w-0">
          <div className="font-editorial text-lg font-normal text-[#EEEEEF] leading-tight truncate">
            {lead.service_type}
          </div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-[#C9C6C6] mt-1">
            {lead.zip_code}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {!claimed && (
            <span className="text-[9px] uppercase tracking-[0.2em] font-semibold bg-[#0E93EC] text-white px-1.5 py-0.5">
              new
            </span>
          )}
          <div className="text-[10px] text-[#C9C6C6] flex items-center gap-1">
            <Clock className="w-3 h-3" />
            {timeAgo(lead.created_at)}
          </div>
        </div>
      </div>
      <div className="text-sm text-[#EEEEEF] font-medium truncate">
        {lead.name}
      </div>
      {lead.project_details && (
        <p className="mt-1 text-xs text-[#C9C6C6] line-clamp-2 leading-snug">
          {lead.project_details}
        </p>
      )}
      <div className="mt-3 flex items-center gap-3 text-[11px] text-[#C9C6C6]">
        <span className="inline-flex items-center gap-1">
          <Mail className="w-3 h-3" /> {lead.email || "email on file"}
        </span>
      </div>
    </motion.button>
  );
}

/* ============================================================
 *  Kanban
 * ============================================================ */
function Kanban({ leads, onOpen }) {
  const grouped = useMemo(() => {
    const g = { new: [], contacted: [], quoted: [], won: [], lost: [] };
    leads.forEach((l) => {
      const s = l.state?.status || "new";
      if (g[s]) g[s].push(l);
    });
    return g;
  }, [leads]);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-8">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
        {COLUMNS.map((col) => (
          <div
            key={col.id}
            className="bg-[#010204] border border-[#C9C6C6]/10 flex flex-col min-h-[400px]"
            data-testid={`column-${col.id}`}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-[#C9C6C6]/10">
              <div className="flex items-center gap-2">
                <span
                  className="w-2 h-2"
                  style={{ backgroundColor: col.accent }}
                />
                <span className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
                  {col.label}
                </span>
              </div>
              <span className="text-[11px] font-mono-alt text-[#C9C6C6]">
                {grouped[col.id].length}
              </span>
            </div>
            <div className="p-3 space-y-3 flex-1 overflow-y-auto max-h-[70vh]">
              {grouped[col.id].length === 0 && (
                <div className="text-xs text-[#C9C6C6] italic px-2 py-4">
                  No leads here yet.
                </div>
              )}
              {grouped[col.id].map((l) => (
                <LeadCard key={l.id} lead={l} onOpen={onOpen} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
 *  Estimate Panel — inside the lead drawer, contractor drafts a bid
 * ============================================================ */
function EstimatePanel({ leadId, homeownerId }) {
  const [mine, setMine] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [items, setItems] = useState([{ description: "", qty: 1, unit_price: "" }]);
  const [scope, setScope] = useState("");
  const [days, setDays] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/workspace/estimates?lead_id=${leadId}`
      );
      setMine(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    const valid = items.filter(
      (i) => i.description.trim() && Number(i.qty) > 0 && i.unit_price !== "" && Number(i.unit_price) >= 0
    );
    if (valid.length === 0) {
      toast.error("Add at least one line item (description, qty, unit price)");
      return;
    }
    if (!scope.trim() || scope.trim().length < 5) {
      toast.error("Scope of work is required");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/workspace/estimates`,
        {
          lead_id: leadId,
          items: valid.map((i) => ({
            description: i.description.trim(),
            qty: Number(i.qty),
            unit_price: Number(i.unit_price),
          })),
          scope: scope.trim(),
          estimated_days: days ? Number(days) : null,
        },
        { withCredentials: true }
      );
      toast.success("Estimate sent to the homeowner");
      setItems([{ description: "", qty: 1, unit_price: "" }]);
      setScope("");
      setDays("");
      setShowForm(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send estimate");
    } finally {
      setBusy(false);
    }
  };

  if (!homeownerId) {
    return (
      <div className="border border-[#C9C6C6]/10 bg-[#010204] p-4 text-sm text-[#C9C6C6]">
        This lead was submitted anonymously — the homeowner hasn&apos;t signed up
        yet, so you can&apos;t send them an in-app estimate. Reach out by phone or email.
      </div>
    );
  }

  const statusMap = {
    sent: { label: "Sent · awaiting decision", color: "text-[#0E93EC]" },
    accepted: { label: "Accepted 🎉", color: "text-[#0E93EC]" },
    declined: { label: "Declined", color: "text-[#C9C6C6]" },
    withdrawn: { label: "Withdrawn", color: "text-[#C9C6C6]" },
  };

  return (
    <div data-testid="estimate-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
          Estimates
        </div>
        {!showForm && (
          <Button
            onClick={() => setShowForm(true)}
            className="h-9 rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white px-4 text-xs uppercase tracking-[0.2em] font-semibold"
            data-testid="draft-estimate-btn"
          >
            <DollarSign className="w-3.5 h-3.5 mr-1" /> Draft estimate
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-xs text-[#C9C6C6]">Loading…</div>
      ) : mine.length === 0 && !showForm ? (
        <div className="text-xs text-[#C9C6C6] italic">
          No estimate sent to this homeowner yet.
        </div>
      ) : (
        <div className="space-y-2">
          {mine.map((e) => {
            const meta = statusMap[e.status] || statusMap.sent;
            return (
              <div
                key={e.id}
                className="border border-[#C9C6C6]/10 bg-[#0D1421] p-3"
                data-testid={`my-estimate-${e.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="font-editorial text-lg text-[#EEEEEF] tracking-tighter">
                    ${Number(e.price_low).toLocaleString()}
                    {Number(e.price_high) > Number(e.price_low) && (
                      <>
                        <span className="text-[#C9C6C6]"> – </span>
                        ${Number(e.price_high).toLocaleString()}
                      </>
                    )}
                  </div>
                  <div className={`text-[10px] uppercase tracking-[0.2em] font-semibold ${meta.color}`}>
                    {meta.label}
                  </div>
                </div>
                <p className="mt-1 text-xs text-[#EEEEEF] line-clamp-3 whitespace-pre-wrap">
                  {e.scope}
                </p>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="mt-4 border border-[#0E93EC]/40 bg-[#0D1421] p-4 space-y-3">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
              Line items
            </Label>
            <div className="mt-1 border border-[#C9C6C6]/15 bg-[#010204] p-3">
              <LineItemsEditor items={items} setItems={setItems} />
            </div>
          </div>
          <div className="w-40">
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
              Days (opt.)
            </Label>
            <Input
              type="number"
              min={0}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              placeholder="e.g. 2"
              className="mt-1 h-10 rounded-none border-[#C9C6C6]/15 focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
              data-testid="estimate-days"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
              Scope of work
            </Label>
            <Textarea
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              rows={5}
              placeholder="Describe what's included: materials, labor, warranty. E.g. 'Replace 40-gal water heater with equivalent unit. Includes haul-away and permit.'"
              className="mt-1 bg-[#010204] border-[#C9C6C6]/15 rounded-none focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
              data-testid="estimate-scope"
            />
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={submit}
              disabled={busy}
              className="rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-11 px-5"
              data-testid="estimate-send"
            >
              {busy ? "Sending…" : "Send estimate"}
            </Button>
            <Button
              onClick={() => setShowForm(false)}
              variant="ghost"
              className="rounded-none h-11 px-4 text-[#C9C6C6] hover:text-[#EEEEEF]"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============================================================
 *  Contractor Jobs (accepted / in progress / completed)
 * ============================================================ */
function ContractorJobs({ user }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/workspace/jobs`, {
        withCredentials: true,
      });
      setJobs(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const sign = async (jobId) => {
    try {
      await axios.post(
        `${API}/jobs/${jobId}/signoff`,
        { party: "contractor" },
        { withCredentials: true }
      );
      toast.success("Signed off. Job moves to complete once the homeowner signs too.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not sign");
    }
  };

  if (loading) return null;
  if (jobs.length === 0) return null;

  return (
    <div
      className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 sm:pt-8"
      data-testid="contractor-jobs-section"
    >
      <div className="border border-[#C9C6C6]/10 bg-[#0D1421]">
        <button
          onClick={() => setExpanded((e) => !e)}
          className="w-full px-4 sm:px-6 py-4 flex items-center justify-between border-b border-[#C9C6C6]/10"
        >
          <div className="flex items-center gap-3">
            <Briefcase className="w-4 h-4 text-[#0E93EC]" />
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6]">
              Awarded jobs
            </div>
            <div className="font-editorial text-2xl text-[#EEEEEF] tracking-tighter">
              {jobs.length}
            </div>
          </div>
          <ChevronDown className={`w-4 h-4 text-[#C9C6C6] transition-transform ${expanded ? "rotate-180" : ""}`} />
        </button>
        {expanded && (
          <div className="p-4 sm:p-6 space-y-4">
            {jobs.map((j) => (
              <ContractorJobCard key={j.id} j={j} sign={sign} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ContractorJobCard({ j, sign }) {
  const [unreadKey, setUnreadKey] = useState(0);
  const you = j.agreement_contractor_signed;
  const them = j.agreement_homeowner_signed;
  const status = j.status;
  const statusColors = {
    accepted: "bg-[#0E93EC] text-[#EEEEEF]",
    in_progress: "bg-[#0E93EC] text-[#EEEEEF]",
    completed: "bg-[#EEEEEF]/15 text-white",
  };
  return (
    <div
      className="border border-[#C9C6C6]/10 bg-[#010204] p-4"
      data-testid={`contractor-job-${j.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`text-[10px] uppercase tracking-[0.2em] font-semibold px-2 py-1 ${statusColors[status] || "bg-[#C9C6C6]/30 text-white"}`}
            >
              {status.replace("_", " ")}
            </span>
            <span className="text-[11px] font-mono-alt text-[#C9C6C6]">
              #{j.invoice_number}
            </span>
            <UnreadBadge jobId={j.id} refreshKey={unreadKey} />
          </div>
          <div className="mt-2 font-editorial text-xl text-[#EEEEEF] tracking-tight">
            {j.lead?.service_type || "Home project"}
          </div>
          <div className="text-xs text-[#C9C6C6]">
            {j.lead?.name} · {j.lead?.zip_code}
          </div>
          <p className="mt-2 text-sm text-[#EEEEEF] line-clamp-2 max-w-lg">
            {j.scope}
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6]">
            Estimate
          </div>
          <div className="font-editorial text-lg text-[#EEEEEF] tracking-tighter">
            ${Number(j.price_low).toLocaleString()}–${Number(j.price_high).toLocaleString()}
          </div>
        </div>
      </div>
      <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div
          className={`p-3 border ${you ? "border-[#0E93EC] bg-[#0E93EC]/5" : "border-[#C9C6C6]/10"}`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">You</div>
          <div className="flex items-center gap-2 text-sm">
            {you ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#0E93EC]" />
                <span>Signed off</span>
              </>
            ) : (
              <span className="text-[#C9C6C6]">Not yet signed</span>
            )}
          </div>
        </div>
        <div
          className={`p-3 border ${them ? "border-[#0E93EC] bg-[#0E93EC]/5" : "border-[#C9C6C6]/10"}`}
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">Homeowner</div>
          <div className="flex items-center gap-2 text-sm">
            {them ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-[#0E93EC]" />
                <span>Signed off</span>
              </>
            ) : (
              <span className="text-[#C9C6C6]">Not yet signed</span>
            )}
          </div>
        </div>
      </div>
      {status !== "completed" && !you && (
        <Button
          onClick={() => sign(j.id)}
          className="mt-4 rounded-none bg-[#0E93EC] hover:bg-[#0E2F5F] text-white h-10 px-5"
          data-testid={`contractor-sign-${j.id}`}
        >
          Mark my side complete
        </Button>
      )}
      <Link
        to={`/workspace/jobs/${j.id}`}
        className="mt-4 inline-flex items-center gap-2 border border-[#0E93EC]/50 text-[#0E93EC] hover:bg-[#0E93EC] hover:text-white px-4 h-10 text-[11px] uppercase tracking-[0.2em] font-semibold transition-colors"
        data-testid={`open-crm-${j.id}`}
      >
        <Briefcase className="w-3.5 h-3.5" /> Open in CRM
      </Link>
      {j.review && j.review.status === "approved" && (
        <div className="mt-4 border-l-2 border-[#0E93EC] bg-[#0D1421] px-3 py-2">
          <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC] mb-1">
            Review · {j.review.rating}★
          </div>
          <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap">
            {j.review.body}
          </p>
        </div>
      )}
      <div className="mt-4">
        <MessageBoard
          jobId={j.id}
          viewerRole="contractor"
          jobStatus={j.status}
          onRead={() => setUnreadKey(Date.now())}
        />
      </div>
    </div>
  );
}

/* ============================================================
 *  Lead Drawer — details, status change, notes
 * ============================================================ */
function LeadDrawer({ lead, open, onOpenChange, onUpdated, onPaywall }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setNote("");
  }, [open, lead?.id]);

  if (!lead) return null;

  const changeStatus = async (status) => {
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/workspace/leads/${lead.id}/state`,
        { status }
      );
      onUpdated(lead.id, data);
      toast.success(`Moved to ${status}`);
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not update. Try again.";
      toast.error(typeof detail === "string" ? detail : "Update failed");
      // 402 → paywall: refresh user so the paywall banner appears
      if (err?.response?.status === 402 && onPaywall) {
        onPaywall();
      }
    } finally {
      setBusy(false);
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try {
      const { data } = await axios.patch(
        `${API}/workspace/leads/${lead.id}/state`,
        { note: note.trim() }
      );
      onUpdated(lead.id, data);
      setNote("");
      toast.success("Note saved");
    } catch (err) {
      const detail =
        err?.response?.data?.detail || "Could not save note.";
      toast.error(typeof detail === "string" ? detail : "Note failed");
    } finally {
      setBusy(false);
    }
  };

  const state = lead.state || {};
  const notes = state.notes || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl bg-[#0D1421] p-0 overflow-y-auto"
        data-testid="lead-drawer"
      >
        <SheetHeader className="p-6 border-b border-[#C9C6C6]/10 space-y-1 text-left">
          <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC]">
            {lead.service_type} · {lead.zip_code}
          </div>
          <SheetTitle className="font-editorial text-3xl font-light text-[#EEEEEF] tracking-tighter">
            {lead.name}
          </SheetTitle>
          <SheetDescription className="text-[#C9C6C6] text-xs">
            Received {timeAgo(lead.created_at)} · current status:{" "}
            <b className="text-[#EEEEEF] uppercase tracking-wide">
              {state.status || "new"}
            </b>
          </SheetDescription>
        </SheetHeader>

        <div className="p-6 space-y-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <a
              href={`tel:${lead.phone}`}
              className="border border-[#C9C6C6]/15 p-4 hover:border-[#0E93EC] transition-colors"
              data-testid="lead-call"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
                Call
              </div>
              <div className="flex items-center gap-2 text-[#EEEEEF] font-medium">
                <Phone className="w-4 h-4 text-[#0E93EC]" />
                {lead.phone}
              </div>
            </a>
            <a
              href={`mailto:${lead.email}`}
              className="border border-[#C9C6C6]/15 p-4 hover:border-[#0E93EC] transition-colors"
              data-testid="lead-email-link"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
                Email
              </div>
              <div className="flex items-center gap-2 text-[#EEEEEF] font-medium truncate">
                <Mail className="w-4 h-4 text-[#0E93EC]" />
                {lead.email}
              </div>
            </a>
            <a
              href={`sms:${lead.phone}`}
              className="border border-[#C9C6C6]/15 p-4 hover:border-[#0E93EC] transition-colors"
              data-testid="lead-sms-link"
            >
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
                Text
              </div>
              <div className="flex items-center gap-2 text-[#EEEEEF] font-medium">
                <MessageSquare className="w-4 h-4 text-[#0E93EC]" />
                Send SMS
              </div>
            </a>
            <div className="border border-[#C9C6C6]/15 p-4">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#C9C6C6] mb-1">
                ZIP
              </div>
              <div className="flex items-center gap-2 text-[#EEEEEF] font-medium">
                <MapPin className="w-4 h-4 text-[#0E93EC]" />
                {lead.zip_code}
              </div>
            </div>
          </div>

          {lead.project_details && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-2">
                Project details
              </div>
              <p className="text-[15px] text-[#EEEEEF] leading-relaxed">
                {lead.project_details}
              </p>
            </div>
          )}

          <EstimatePanel leadId={lead.id} homeownerId={lead.homeowner_id} />

          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#C9C6C6] mb-3">
              Move to
            </div>
            <div className="flex flex-wrap gap-2" data-testid="lead-status-actions">
              {COLUMNS.map((c) => (
                <button
                  key={c.id}
                  onClick={() => changeStatus(c.id)}
                  disabled={busy || state.status === c.id}
                  className={`text-[11px] uppercase tracking-[0.2em] font-semibold px-3 py-2 border transition-colors ${
                    state.status === c.id
                      ? "bg-[#010204] text-white border-[#C9C6C6]"
                      : "bg-transparent text-[#EEEEEF] border-[#C9C6C6]/20 hover:border-[#0E93EC] hover:text-[#0E93EC]"
                  }`}
                  data-testid={`status-${c.id}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#EEEEEF]">
              Add a note
            </Label>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder="Site visit scheduled Thursday 3pm. Quote sent for $850."
              className="mt-2 bg-[#010204] border-0 rounded-none focus-visible:ring-2 focus-visible:ring-[#0E93EC] focus-visible:ring-offset-0"
              data-testid="lead-note-input"
            />
            <Button
              onClick={addNote}
              disabled={busy || !note.trim()}
              className="mt-3 bg-[#0E93EC] hover:bg-[#0E2F5F] text-white rounded-none h-11 px-6 font-medium"
              data-testid="lead-note-save"
            >
              Save note
            </Button>

            {notes.length > 0 && (
              <div className="mt-6 space-y-3" data-testid="lead-notes-list">
                {notes.map((n, i) => (
                  <div
                    key={i}
                    className="bg-[#010204] border-l-2 border-[#0E93EC] p-3"
                  >
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9C6C6] mb-1">
                      {timeAgo(n.at)}
                    </div>
                    <p className="text-sm text-[#EEEEEF] whitespace-pre-wrap">
                      {n.text}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

/* ============================================================
 *  Empty State
 * ============================================================ */
function EmptyState({ user }) {
  return (
    <div className="max-w-[900px] mx-auto px-6 py-24 text-center">
      <div className="text-[10px] uppercase tracking-[0.25em] font-semibold text-[#0E93EC] mb-6">
        — Quiet in here
      </div>
      <h2 className="font-editorial text-5xl md:text-6xl font-light tracking-tighter text-[#EEEEEF] leading-[0.95]">
        No local leads
        <br />
        <span className="italic">yet.</span>
      </h2>
      <p className="mt-8 text-[#C9C6C6] leading-relaxed max-w-lg mx-auto">
        The moment a homeowner in your service area submits a request, it'll
        show up here. In the meantime, make sure your profile is dialed —{" "}
        <b>trade, ZIP, and phone number</b> matter for matching.
      </p>
    </div>
  );
}

/* ============================================================
 *  Workspace page
 * ============================================================ */
export default function Workspace() {
  const { user, loading, logout, checkAuth } = useAuth();
  const [leads, setLeads] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selectedLead, setSelectedLead] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [tradeFilter, setTradeFilter] = useState("mine");
  const [query, setQuery] = useState("");

  const filteredLeads = useMemo(() => {
    let out = leads;
    if (tradeFilter === "mine" && user?.trade) {
      out = out.filter((l) => l.service_type === user.trade);
    } else if (tradeFilter !== "all" && tradeFilter !== "mine") {
      out = out.filter((l) => l.service_type === tradeFilter);
    }
    const q = query.trim().toLowerCase();
    if (q) {
      out = out.filter((l) =>
        [l.service_type, l.name, l.zip_code, l.project_details]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(q)
      );
    }
    return out;
  }, [leads, tradeFilter, query, user]);

  const load = useCallback(async () => {
    setDataLoading(true);
    try {
      const [leadsResp, sumResp] = await Promise.all([
        axios.get(`${API}/workspace/leads`),
        axios.get(`${API}/workspace/summary`),
      ]);
      setLeads(leadsResp.data);
      setSummary(sumResp.data);
    } catch (err) {
      toast.error("Could not load workspace");
    } finally {
      setDataLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const openLead = (lead) => {
    setSelectedLead(lead);
    setDrawerOpen(true);
  };

  const onLeadUpdated = (leadId, data) => {
    setLeads((prev) =>
      prev.map((l) => (l.id === leadId ? { ...l, state: data.state } : l))
    );
    setSelectedLead((prev) =>
      prev && prev.id === leadId ? { ...prev, state: data.state } : prev
    );
    setSummary((prev) =>
      prev ? { ...prev, free_leads_remaining: data.free_leads_remaining } : prev
    );
    // Refresh summary counts
    axios.get(`${API}/workspace/summary`).then((r) => setSummary(r.data)).catch(() => {});
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0D1421] text-[#C9C6C6]">
        <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC] mr-2" />
        Loading workspace…
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/workspace/login" replace />;
  }

  return (
    <div className="min-h-screen bg-[#0D1421]" data-testid="workspace-page">
      <TopBar user={user} onLogout={logout} />
      <SubscriptionBanner user={user} summary={summary} />
      <FoundingUpgradeStrip user={user} />
      <SummaryStrip summary={summary} />
      <ReferralsStrip user={user} />
      <VerificationPanel onStatusChange={checkAuth} />
      <ContractorJobs user={user} />
      {dataLoading ? (
        <div className="p-12 flex items-center gap-3 text-[#C9C6C6]">
          <Loader2 className="w-5 h-5 animate-spin text-[#0E93EC]" />
          Loading leads…
        </div>
      ) : leads.length === 0 ? (
        <EmptyState user={user} />
      ) : (
        <>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10 pt-6 flex flex-col sm:flex-row sm:items-center gap-3"
            data-testid="opportunities-filter">
            <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#0E93EC]">
              Work opportunities · {filteredLeads.length}
            </div>
            <div className="flex-1" />
            <div className="flex gap-2">
              <div className="relative">
                <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#C9C6C6]" />
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search zip, details, name…"
                  className="h-10 w-52 rounded-none border-[#C9C6C6]/15 bg-[#010204] text-[#EEEEEF] pl-8"
                  data-testid="opportunities-search"
                />
              </div>
              <Select value={tradeFilter} onValueChange={setTradeFilter}>
                <SelectTrigger
                  className="h-10 w-44 rounded-none border-[#C9C6C6]/15 bg-[#010204] text-[#EEEEEF]"
                  data-testid="trade-filter"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#010204] border-[#C9C6C6]/15 text-[#EEEEEF]">
                  <SelectItem value="mine">My trade{user?.trade ? ` (${user.trade})` : ""}</SelectItem>
                  <SelectItem value="all">All trades</SelectItem>
                  {TRADE_OPTIONS.map((t) => (
                    <SelectItem key={t} value={t}>{t}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <Kanban leads={filteredLeads} onOpen={openLead} />
        </>
      )}

      <LeadDrawer
        lead={selectedLead}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onUpdated={onLeadUpdated}
        onPaywall={checkAuth}
      />
    </div>
  );
}
